import { type ServerMessage, type PageContext } from './chatTypes';

export class ChatApiManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(
    private sessionId: string,
    private onMessage: (data: ServerMessage) => void,
    private onStatusChange: (connected: boolean) => void
  ) {}

  connect(): void {
    if (this.disposed) return;

    const wsUrl = import.meta.env.VITE_FAQ_WS_URL;
    this.ws = new WebSocket(`${wsUrl}/ws/chat/${this.sessionId}`);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.onStatusChange(true);
      this.startHeartbeat();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const data: ServerMessage = JSON.parse(event.data as string);
        if (data.type !== 'pong') {
          this.onMessage(data);
        }
      } catch {
        // ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.onStatusChange(false);
      this.stopHeartbeat();
      if (!this.disposed) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onclose will be called after onerror
    };
  }

  send(data: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  disconnect(): void {
    this.disposed = true;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;
    setTimeout(() => this.connect(), delay);
  }
}

export class ChatApiRestFallback {
  private sessionId: string | null = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private lastTimestamp: string | null = null;
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_FAQ_API_URL as string;
  }

  async startSession(context: PageContext, clientId?: string): Promise<ServerMessage> {
    const res = await fetch(`${this.baseUrl}/chat/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context, client_id: clientId }),
    });
    const data: ServerMessage = await res.json();
    this.sessionId = data.session_id ?? null;
    return data;
  }

  async resumeSession(sessionId: string): Promise<ServerMessage> {
    const res = await fetch(`${this.baseUrl}/chat/resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const data = await res.json();
    this.sessionId = sessionId;
    return data;
  }

  setSessionId(id: string): void {
    this.sessionId = id;
  }

  setLastTimestamp(ts: string): void {
    this.lastTimestamp = ts;
  }

  async sendMessage(text: string): Promise<ServerMessage> {
    const res = await fetch(`${this.baseUrl}/chat/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: this.sessionId, text }),
    });
    return res.json();
  }

  startPolling(onMessage: (msgs: ServerMessage[], status?: string, operatorName?: string) => void): void {
    this.stopPolling();
    this._polling = true;

    const poll = async () => {
      if (!this._polling || !this.sessionId) return;
      try {
        const res = await fetch(`${this.baseUrl}/chat/poll/${this.sessionId}`);
        const data = await res.json();

        onMessage(
          data.messages || [],
          data.status || null,
          data.operator_name || null
        );
      } catch {
        // silently ignore polling errors
      }
      if (this._polling) {
        this.pollInterval = window.setTimeout(poll, 3000);
      }
    };

    // Первый poll сразу
    this.pollInterval = window.setTimeout(poll, 1000);
  }

  private _polling = false;

  stopPolling(): void {
    this._polling = false;
    if (this.pollInterval) {
      clearTimeout(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async resolveSession(): Promise<void> {
    await fetch(`${this.baseUrl}/chat/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: this.sessionId }),
    });
  }
}
